'use client';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/shadcn/avatar';
import { Button } from '@/components/ui/shadcn/button';
import { type ImpersonatedUser } from '@/types';
import { router } from '@inertiajs/react';
import { AlertTriangle, X } from 'lucide-react';

interface ImpersonationBannerProps {
    impersonatedUser: ImpersonatedUser;
}

export function ImpersonationBanner({ impersonatedUser }: ImpersonationBannerProps) {
    const handleStopImpersonating = () => {
        router.post(
            '/impersonate/stop',
            {},
            {
                preserveScroll: true,
            },
        );
    };

    return (
        <div className="border-border bg-warning-subtle border-b">
            <div className="mx-auto flex h-12 items-center justify-between px-4 md:max-w-7xl">
                <div className="flex items-center gap-3">
                    <AlertTriangle className="text-warning-subtle-foreground h-5 w-5" />
                    <div className="flex items-center gap-2">
                        <span className="text-warning-subtle-foreground text-sm font-medium">Imitating:</span>
                        <div className="flex items-center gap-2">
                            <Avatar className="h-6 w-6">
                                <AvatarImage
                                    src={impersonatedUser.avatar || ''}
                                    alt={`${impersonatedUser.first_name} ${impersonatedUser.last_name}`}
                                />
                                <AvatarFallback className="text-xs">
                                    {impersonatedUser.first_name?.[0]}
                                    {impersonatedUser.last_name?.[0]}
                                </AvatarFallback>
                            </Avatar>
                            <span className="text-warning-subtle-foreground text-sm font-semibold">
                                {impersonatedUser.first_name} {impersonatedUser.last_name}
                            </span>
                            {impersonatedUser.role && (
                                <span className="bg-warning-subtle text-warning-subtle-foreground rounded px-2 py-0.5 text-xs">
                                    {impersonatedUser.role}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleStopImpersonating}
                    className="border-border bg-warning-subtle text-warning-subtle-foreground hover:bg-accent"
                >
                    <X className="mr-2 h-4 w-4" />
                    Stop Imitating
                </Button>
            </div>
        </div>
    );
}
